/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * The list component for Select
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import React, { Fragment, memo, useRef } from 'react';
import type { RefObject } from 'react';

import classNames from 'classnames';
import { FixedSizeList } from 'react-window';

import { FIXED_SIZE_LIST_HEIGHT, SELECT_LIST_VERTICAL_PADDING } from '@components/common/Select/constants';
import InfinityScrollWrapper from '@components/InfinityScrollWrapper/InfinityScrollWrapper';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import { usePreventWindowScroll } from '@modules/app/hooks/usePreventWindowScroll';

import { EllipseText, SelectItem } from '../../index';
import { IOptionItemProps, ISelectVirtualListProps } from '../Select';
import { SelectItemStyled } from '../styled/SelectStyled';

export interface ISelectListVirtualListProps extends ISelectVirtualListProps {
    onScrollEvent: () => void;
}

export interface ISelectListProps {
    options: IOptionItemProps[];
    selectedOption?: IOptionItemProps;
    selectItem: (item: IOptionItemProps) => void;
    virtualListProps?: ISelectListVirtualListProps;
}

export const SelectList = (props: ISelectListProps) => {
    const listBodyRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    usePreventWindowScroll(listBodyRef);

    const keyDownHandler = (event: React.KeyboardEvent, item: IOptionItemProps): void => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            event.stopPropagation();
            props.selectItem(item);
        }
    };

    const listHeight =
        props.virtualListProps?.rowHeight && props.options.length
            ? props.options.length * props.virtualListProps.rowHeight + 2 * SELECT_LIST_VERTICAL_PADDING
            : 0;

    return (
        <Fragment>
            {props.virtualListProps ? (
                <InfinityScrollWrapper
                    hasMore={props.virtualListProps.hasMore}
                    isLoading={props.virtualListProps.isLoading}
                    sizeElement={listBodyRef}
                    watchElement={listBodyRef}
                    onScrollEvent={props.virtualListProps.onScrollEvent}
                >
                    <FixedSizeList
                        className="select__select-list list virtual"
                        height={listHeight > FIXED_SIZE_LIST_HEIGHT ? FIXED_SIZE_LIST_HEIGHT : listHeight}
                        itemCount={props.options.length}
                        itemSize={props.virtualListProps.rowHeight}
                        outerRef={listBodyRef}
                        width="100%"
                    >
                        {({ index, style }) => (
                            <SelectItem
                                className={classNames('select__select-item', {
                                    disable: props.options[index]?.disabled,
                                })}
                                key={props.options[index]?.value}
                                selected={
                                    props.selectedOption && props.selectedOption.value === props.options[index]?.value
                                }
                                style={style}
                                onClick={() => props.selectItem(props.options[index])}
                                onKeyDown={(e: React.KeyboardEvent) => keyDownHandler(e, props.options[index])}
                            >
                                <EllipseText title={props.options[index]?.label}>
                                    {props.options[index]?.label}
                                </EllipseText>
                            </SelectItem>
                        )}
                    </FixedSizeList>
                </InfinityScrollWrapper>
            ) : (
                <ul className="select__select-list list">
                    {props.options.map((item: IOptionItemProps) => {
                        return (
                            <SelectItem
                                className={classNames('select__select-item', { disable: item.disabled })}
                                data-name={item.dataName}
                                key={item.value}
                                selected={props.selectedOption && props.selectedOption.value === item.value}
                                onClick={() => props.selectItem(item)}
                                onKeyDown={(e: React.KeyboardEvent) => keyDownHandler(e, item)}
                            >
                                <SelectItemStyled>
                                    <EllipseText>{item.label}</EllipseText>
                                    {item.labelIcon}
                                </SelectItemStyled>
                            </SelectItem>
                        );
                    })}
                </ul>
            )}
        </Fragment>
    );
};

SelectList.defaultProps = {
    selectItem: () => null,
};

export default memo(SelectList);
